/*
  Zahlen deutsch lesen und schreiben. Bewusst ohne Bibliothek — wie datum.js.

  ---------------------------------------------------------------
  Warum nicht einfach `toLocaleString("de-DE")`
  ---------------------------------------------------------------

  Das Anzeigen könnte der Browser übernehmen. Das LESEN aber nicht: Es gibt
  keine Umkehrung von `toLocaleString`, und genau die wird hier gebraucht,
  sobald jemand „1.234,50" in eine Zelle tippt. Beides von Hand zu machen
  hält Hin- und Rückweg zusammen — sonst formatiert die eine Seite anders,
  als die andere liest, und der Fehler zeigt sich erst nach dem Neuladen.

  ---------------------------------------------------------------
  Die deutsche Schreibweise ist eine Falle
  ---------------------------------------------------------------

      1.234,50   →   eintausendzweihundertvierunddreißig Komma fünf
      1,234.50   →   dasselbe auf Englisch

  Punkt und Komma haben vertauschte Rollen. `parseFloat("1.234,50")` liefert
  1.234 — falsch um den Faktor tausend, ohne jede Fehlermeldung. Das ist
  genau die Sorte stiller Fehler, gegen die ein Haushaltsantrag nicht
  gefeit ist.
*/

export const FORMATE = ["text", "zahl", "euro", "prozent"];

export const FORMATNAMEN = {
  text: "Text",
  zahl: "Zahl",
  euro: "Euro",
  prozent: "Prozent",
};

/* Wie viele Nachkommastellen ein Format zeigt. */
const STELLEN = { zahl: null, euro: 2, prozent: 1 };

/*
  Eine getippte Zahl lesen. Gibt `null` zurück, wenn es keine ist —
  „null" heißt hier ausdrücklich „keine Zahl" und nicht „die Null".

  Erlaubt sind: Vorzeichen, Tausenderpunkte, ein Komma, ein angehängtes
  Prozent- oder Euro-Zeichen. Verboten ist alles, was zweideutig wäre.
*/
export function liesZahl(text) {
  if (typeof text === "number") return Number.isFinite(text) ? text : null;
  if (typeof text !== "string") return null;

  let roh = text.trim();
  if (!roh) return null;

  /* Ein angehängtes Zeichen darf mitkommen — getippt wird oft „12 €". */
  let prozent = false;
  if (roh.endsWith("%")) {
    prozent = true;
    roh = roh.slice(0, -1).trim();
  } else if (roh.endsWith("€")) {
    roh = roh.slice(0, -1).trim();
  }

  if (!/^[+-]?[0-9.]*,?[0-9]*$/.test(roh)) return null;

  const minus = roh.startsWith("-");
  if (roh.startsWith("+") || roh.startsWith("-")) roh = roh.slice(1);

  const komma = roh.indexOf(",");
  if (komma !== roh.lastIndexOf(",")) return null;

  const ganz = komma === -1 ? roh : roh.slice(0, komma);
  const bruch = komma === -1 ? "" : roh.slice(komma + 1);

  if (!ganz && !bruch) return null;
  if (bruch.includes(".")) return null;

  /*
    Der Tausenderpunkt muss stimmen: 1.234.567 ist eine Zahl, 1.23 nicht.
    Wer das durchlässt, liest „1.23" als 123 statt als Unsinn — und ein
    Tippfehler wird still zu einem hundertfachen Betrag.
  */
  if (ganz.includes(".")) {
    const gruppen = ganz.split(".");
    if (gruppen.length < 2) return null;
    if (gruppen[0].length < 1 || gruppen[0].length > 3) return null;
    if (gruppen.slice(1).some((g) => g.length !== 3)) return null;
  }

  const zahl = Number(`${ganz.replace(/\./g, "") || "0"}.${bruch || "0"}`);
  if (!Number.isFinite(zahl)) return null;

  const mitVorzeichen = minus ? -zahl : zahl;
  /* „15 %" getippt heißt der Anteil 0,15 — so rechnet auch Excel. */
  return prozent ? mitVorzeichen / 100 : mitVorzeichen;
}

/*
  Eine Zahl in deutscher Schreibweise ausgeben — mit Tausenderpunkten.

  `stellen === null` heißt: so viele Nachkommastellen wie nötig, höchstens
  zehn. Ein Ergebnis von 1/3 soll nicht mit siebzehn Stellen dastehen, aber
  eine glatte 42 auch nicht als 42,00.
*/
export function schreibeZahl(zahl, stellen = null) {
  if (typeof zahl !== "number" || !Number.isFinite(zahl)) return "";

  const gerundet = stellen === null ? runde(zahl, 10) : Number(zahl.toFixed(stellen));
  const minus = gerundet < 0 || Object.is(gerundet, -0);
  const betrag = Math.abs(gerundet);

  const feste = stellen === null ? String(betrag) : betrag.toFixed(stellen);
  const [ganz, bruch = ""] = feste.split(".");

  const mitPunkten = ganz.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const text = bruch ? `${mitPunkten},${bruch}` : mitPunkten;
  return minus ? `-${text}` : text;
}

/* Rundung auf Stellen, ohne die Exponentialschreibweise von `toFixed` zu erben. */
function runde(zahl, stellen) {
  const faktor = 10 ** stellen;
  return Math.round(zahl * faktor) / faktor;
}

/*
  Was in der Zelle steht.

  `wert` ist das Ergebnis der Rechnung: `{ art, wert }`. Das Format der
  Spalte entscheidet nur über die DARSTELLUNG — gerechnet wird immer mit der
  nackten Zahl. Deshalb kann man das Format jederzeit umstellen, ohne dass
  sich ein Ergebnis ändert.
*/
export function formatiere(wert, format = "zahl") {
  if (wert === null || wert === undefined) return "";
  if (typeof wert === "string") return wert;
  if (typeof wert !== "number" || !Number.isFinite(wert)) return "";

  if (format === "text") return schreibeZahl(wert, null);

  if (format === "prozent") {
    return `${schreibeZahl(wert * 100, STELLEN.prozent)} %`;
  }

  if (format === "euro") {
    return `${schreibeZahl(wert, STELLEN.euro)} €`;
  }

  return schreibeZahl(wert, STELLEN.zahl);
}

/*
  Was der Nutzer getippt hat, gedeutet.

  Gibt `{ art: "zahl" | "text", wert }` zurück. In einer Textspalte bleibt
  auch „42" Text — wer eine Spalte auf Text stellt, will Aktenzeichen und
  Postleitzahlen unverändert sehen, nicht als Zahl gerundet.
*/
export function deuteEingabe(text, format = "zahl") {
  const roh = typeof text === "string" ? text.trim() : "";
  if (!roh) return { art: "leer", wert: "" };
  if (format === "text") return { art: "text", wert: roh };

  const zahl = liesZahl(roh);
  if (zahl === null) return { art: "text", wert: roh };
  return { art: "zahl", wert: zahl };
}

/*
  Die Zahl für eine CSV-Datei: mit Komma, aber OHNE Tausenderpunkt und ohne
  angehängtes Zeichen.

  `1.234,50 €` ist für Excel Text, `1234,5` eine Zahl. Das Format ist eine
  Eigenschaft der Ansicht und hat in einer Datei nichts zu suchen — sonst
  bekommt der Empfänger eine Spalte, mit der er nicht rechnen kann.
*/
export function alsCsvZahl(zahl) {
  if (typeof zahl !== "number" || !Number.isFinite(zahl)) return "";
  const gerundet = runde(zahl, 10);
  return String(gerundet).replace(".", ",");
}
